<script>
export default {
  name: 'CustomerTrackingFreelanceGuide',
  props: ['show'],
};
</script>

<template>
  <v-dialog :value="show" fullscreen scrollable hide-overlay transition="dialog-bottom-transition">
    <v-card>
      <v-toolbar dark color="primary">
        <v-btn icon dark @click="$emit('close')">
          <v-icon>close</v-icon>
        </v-btn>
        <v-toolbar-title>Guión de llamadas para autónomos</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn dark flat @click="$emit('close')">Cerrar</v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text style="padding-top: 0" class="freelance-guide">
        <div class="llamada">
          <h2>
            <span class="emoji">📞</span>
            Primera llamada del año – Evaluación y necesidades del autónomo
          </h2>

          <div class="objetivo">
            <h3>
              <span class="emoji">🎯</span>
              Objetivo:
            </h3>
            <ul>
              <li>Conocer su nivel de satisfacción con la asesoría.</li>
              <li>Identificar posibles mejoras o necesidades no cubiertas.</li>
              <li>Ofrecer consejos para optimizar su gestión fiscal, contable o laboral.</li>
            </ul>
          </div>

          <div class="seccion">
            <h3>
              <span class="emoji">📜</span>
              Guion de la llamada:
            </h3>

            <ol>
              <li>
                <p class="destacado">Introducción</p>
                <p>
                  "Hola [nombre], soy [tu nombre] de OpenGes. Quería llamarte porque en OpenGes
                  queremos asegurarnos de que tienes todo el apoyo que necesitas para gestionar tu
                  actividad como autónomo. Además, queremos comentarte algunas ideas que podrían
                  ayudarte a optimizar tu fiscalidad y evitar sorpresas con Hacienda. ¿Te viene bien
                  ahora?"
                </p>
                <p><em>Si el cliente está ocupado:</em></p>
                <p>"No hay problema, dime cuándo te viene mejor y te llamo en otro momento."</p>
              </li>

              <li>
                <p class="destacado">Preguntas clave</p>
                <ul>
                  <li>¿Cómo te ha ido en estos últimos meses con tu actividad?</li>
                  <li>¿Sientes que tienes claridad sobre tus impuestos y obligaciones fiscales?</li>
                  <li>
                    ¿Te resultan cómodos los procesos de facturación y contabilidad, o hay algo que
                    podríamos simplificar?
                  </li>
                  <li>
                    ¿Ha cambiado algo en tu negocio recientemente que debamos tener en cuenta para
                    asesorarte mejor?
                  </li>
                  <li>
                    ¿Tienes previsión de cambios en tu negocio (subida de ingresos, contratación,
                    inversiones, etc.)?
                  </li>
                  <li>
                    ¿Hay alguna gestión o trámite que te gustaría entender mejor o externalizar?
                  </li>
                </ul>
                <p></p>
              </li>

              <li>
                <p class="destacado">Asesoramiento y valor añadido</p>
                <ul>
                  <li>
                    Optimización fiscal: Posibles deducciones, planificación del IRPF, cuotas de
                    autónomos.
                  </li>
                  <li>
                    Subvenciones y ayudas: Oportunidades disponibles según su sector y actividad.
                  </li>
                  <li>
                    Digitalización y herramientas: Recomendaciones para mejorar la gestión
                    administrativa.
                  </li>
                </ul>
                <p></p>
              </li>

              <li>
                <p class="destacado">Preguntas clave sobre el servicio prestado por OpenGes</p>
                <ul>
                  <li>¿Cómo ha sido tu experiencia con nuestro servicio en los últimos meses?</li>
                  <li>
                    ¿Sientes que tienes toda la información y apoyo que necesitas en el área fiscal
                    o contable?
                  </li>
                </ul>
                <p></p>
              </li>

              <li>
                <p class="destacado">Cierre y próximos pasos</p>
                <p>
                  "Gracias por tu tiempo, [nombre]. Me anoto [resumen de necesidades o temas clave]
                  y vamos a [acción que tomará la asesoría]. Si surge algo más, estamos aquí para
                  ayudarte. ¡Seguimos en contacto!"
                </p>
              </li>
            </ol>
          </div>
        </div>

        <div class="llamada">
          <h2>
            <span class="emoji">📞</span>
            Segunda llamada del año – Seguimiento y novedades para el autónomo
          </h2>

          <div class="objetivo">
            <h3>
              <span class="emoji">🎯</span>
              Objetivo:
            </h3>
            <ul>
              <li>Hacer seguimiento de temas comentados en la primera llamada.</li>
              <li>Informar sobre novedades fiscales o cambios legislativos que le afecten.</li>
              <li>Proporcionar recomendaciones para cerrar bien el año fiscal.</li>
            </ul>
          </div>

          <div class="seccion">
            <h3>
              <span class="emoji">📜</span>
              Guion de la llamada:
            </h3>

            <ol>
              <li>
                <p class="destacado">Introducción</p>
                <p>
                  "Hola [nombre], soy [tu nombre] de OpenGes. Hace unos meses hablamos sobre [tema
                  comentado en la primera llamada], y quería saber cómo te ha ido con eso. Además,
                  quería contarte algunas novedades importantes para los autónomos que pueden
                  interesarte. ¿Tienes un momento?"
                </p>
                <p><em>Si no hubo temas pendientes:</em></p>
                <p>
                  "Este año ha habido algunos cambios en normativas para autónomos que pueden
                  afectarte, y quería comentarte algunos puntos clave para que los tengas en
                  cuenta."
                </p>
              </li>

              <li>
                <p class="destacado">Preguntas clave</p>
                <ul>
                  <li>
                    ¿Cómo ha ido el año hasta ahora? ¿Has tenido algún cambio en tu actividad?
                  </li>
                  <li>
                    ¿Cómo vas con la planificación de impuestos para el cierre del año? Ver el
                    rendimiento a la fecha de la llamada.
                  </li>
                  <li>
                    ¿Tienes alguna preocupación o duda sobre la declaración de la renta, deducciones
                    o pagos pendientes?
                  </li>
                  <li>
                    ¿Te gustaría conocer opciones para reducir tu carga fiscal antes de que acabe el
                    año?
                  </li>
                </ul>
                <p></p>
              </li>

              <li>
                <p class="destacado">Información de valor</p>
                <ul>
                  <li>Cambios en la cuota de autónomos, nuevos tramos o bonificaciones.</li>
                  <li>
                    Estrategias para reducir la base imponible antes del cierre del ejercicio.
                  </li>
                  <li>
                    Revisión de posibles deducciones fiscales (formación, teletrabajo, suministros,
                    etc.).
                  </li>
                  <li>Subvenciones o ayudas que puedan estar disponibles.</li>
                  <li>
                    Seguros privados de salud (deducción en IRPF), seguros de responsabilidad civil
                    o cualquier otro tipo que necesiten.
                  </li>
                </ul>
                <p></p>
              </li>

              <li>
                <p class="destacado">Cierre y próximos pasos</p>
                <p>
                  "Me alegro de haber podido ponerte al día, [nombre]. Me quedo con [resumen de
                  puntos clave]. Si necesitas más detalles o quieres que revisemos algo juntos, dime
                  y lo vemos. ¡Un placer hablar contigo!"
                </p>
              </li>
            </ol>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.freelance-guide {
  font-family: Arial, sans-serif;
  line-height: 1.6;
  color: #333;
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}
h1 {
  color: #2c3e50;
  border-bottom: 2px solid #3498db;
  padding-bottom: 10px;
}
h2 {
  color: #2980b9;
  margin-top: 30px;
}
.llamada {
  background-color: #f8f9fa;
  border-left: 4px solid #3498db;
  padding: 15px;
  margin-bottom: 30px;
  border-radius: 0 5px 5px 0;
}
.objetivo {
  background-color: #e8f4f8;
  padding: 15px;
  border-radius: 5px;
  margin-bottom: 20px;
}
.objetivo h3 {
  margin-top: 0;
  color: #2980b9;
}
.objetivo ul {
  margin-bottom: 0;
}
.seccion {
  margin-bottom: 20px;
}
.seccion h3 {
  color: #2c3e50;
  border-bottom: 1px solid #ddd;
  padding-bottom: 5px;
}
.seccion ol {
  padding-left: 20px;
}
.seccion ul {
  list-style-type: circle;
  padding-left: 20px;
}
.destacado {
  font-weight: bold;
  color: #2980b9;
}
.emoji {
  font-size: 1.5em;
  margin-right: 10px;
  vertical-align: middle;
}
</style>
